import { booking } from '../api/booking';
import { isApiConfigured, tokenStore } from '../api';
import { publicSettings } from '../api/publicSettings';
import { haversineKm } from './routeEstimate';

/**
 * InGo's own places (the mobile plan's M4: this one seam serves every
 * screen), when this phone holds an InGo session: its map server, its service
 * area, no Google key. InGo's place search is the app's only one (2 Oct
 * 2026). Null when there is no session or the call failed, and then the
 * caller has no place to give; an empty answer from InGo is an answer.
 */
async function ingoPlaces(run) {
  try {
    if (!isApiConfigured() || !(await tokenStore.read())) return null;

    return { value: await run() };
  } catch (e) {
    console.warn(`[places] InGo places failed ${JSON.stringify({ code: e?.code || null, message: e?.message || String(e) })}`);

    return null;
  }
}

/** One of InGo's places as the screens' one-line label. */
function ingoPlaceLabel(place) {
  return place.detail ? `${place.name}, ${place.detail}` : place.name;
}

/**
 * The countries InGo operates in (its Settings, Regional; InGo ADR 0037), as
 * a label might name one: each name and code, lower case. Read once a
 * session; empty until then, and when it cannot be.
 */
let countryWords = [];

async function loadCountryWords() {
  try {
    const region = await publicSettings.region();
    const words = (region?.countries ?? []).flatMap((c) => [c.name, c.code]).map((w) => String(w).trim().toLowerCase());
    countryWords = words.filter(Boolean);
  } catch {
    // Not reachable: what is known stands.
  }

  return countryWords;
}

/** A label that is only a country we operate in, or "Uganda, Africa". */
function isCountryOnly(s, words) {
  const lower = s.toLowerCase().replace(/\s*,\s*africa$/, '');

  return words.includes(lower);
}

/** "Road, Uganda" without the country, when the country is one of ours. */
function withoutCountry(s, words) {
  const parts = s.split(',');
  const last = parts.length > 1 ? parts[parts.length - 1].trim().toLowerCase() : '';

  return words.includes(last) ? parts.slice(0, -1).join(',').trim() : s;
}

/** Country-only / too-coarse labels we must not use as pickup. */
function isCoarsePlaceLabel(line, words = countryWords) {
  const s = String(line || '')
    .trim()
    .toLowerCase()
    .replace(/\.$/, '');
  if (!s) return true;
  // "Zimbabwe", "UG", "Uganda, Africa": a country with nothing more specific
  return isCountryOnly(s, words);
}

/**
 * Unrecognised / unhelpful reverse-geocode labels common in gated communities.
 * Prefer a nearby named suburb / landmark instead (inDrive-style).
 */
export function isUnrecognizedPlaceLabel(line, words = countryWords) {
  const s = String(line || '').trim();
  if (!s || isCoarsePlaceLabel(s, words)) return true;
  // "Unnamed Road, Zimbabwe" / "Unnamed Street" / "Unnamed Road"
  if (/\bunnamed\b/i.test(s)) return true;
  // Plus codes alone (e.g. "5G8F+XX Zimbabwe") — not human-usable for dispatch
  if (/^[a-z0-9]{4,}\+[a-z0-9]{2,}\b/i.test(s) && !/,/.test(s.split('+')[0] || '')) {
    const withoutPlus = s.replace(/^[a-z0-9+]+\s*,?\s*/i, '').trim();
    if (!withoutPlus || isCoarsePlaceLabel(withoutPlus, words)) return true;
  }
  const bare = withoutCountry(s, words).toLowerCase();
  if (/^[a-z0-9]{4,}\+[a-z0-9]{2,}$/i.test(bare)) return true;
  // Bare "Road" / "Street" with no name
  if (/^(road|street|avenue|drive|lane|close|way)$/i.test(bare)) return true;
  return false;
}

/**
 * @typedef {{ id: string, label: string, lat: number, lng: number }} AddressSuggestion
 */

/**
 * **Address typeahead** — pickup / destination search boxes
 * (**`AddressSuggestInput`**, on **`/request-delivery`**): InGo's place
 * search, each hit with its pin, so the screen places it as chosen with no
 * second lookup. Empty without an InGo session.
 *
 * Nearest first when the screen knows a point to measure from (`near`: the
 * pickup, else where the person stands). Rio, 5 Oct 2026: "people are more
 * likely to make mistakes"; "Seeta" listed Entebbe's Seeta above Mukono's for
 * a client standing in Mukono's.
 *
 * @param {string} query
 * @param {{ limit?: number, near?: { lat: number, lng: number } | null }} [options]
 * @returns {Promise<AddressSuggestion[]>}
 */
export async function fetchAddressAutocompleteSuggestions(query, options = {}) {
  const limit = Math.min(8, Math.max(1, Number(options.limit) || 5));
  const q = String(query || '').trim();
  if (q.length < 2) return [];

  const ingo = await ingoPlaces(() => booking.searchPlaces(q));
  if (!ingo) return [];

  const found = ingo.value.map((place, i) => ({
    id: `ingo-${i}-${place.latitude}-${place.longitude}`,
    label: ingoPlaceLabel(place),
    lat: place.latitude,
    lng: place.longitude,
  }));
  const near = options.near;
  if (near && Number.isFinite(near.lat) && Number.isFinite(near.lng)) {
    const away = (s) => haversineKm(near.lat, near.lng, s.lat, s.lng) ?? Infinity;
    found.sort((a, b) => away(a) - away(b));
  }

  return found.slice(0, limit);
}

/**
 * **GPS → readable address** (e.g. device location label): the place InGo
 * finds at the pin, or '' when it finds none. Callers:
 * **`devicePickupLocation.js`**, customer/driver flows that show “you are here” text.
 */
export async function reverseGeocodeLatLng(latitude, longitude) {
  const lat = Number(latitude);
  const lng = Number(longitude);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
    return '';
  }

  // Read now, so a caller judging the label (`isUnrecognizedPlaceLabel`) knows our countries.
  const [ingo] = await Promise.all([ingoPlaces(() => booking.reversePlace(lat, lng)), loadCountryWords()]);

  return ingo?.value ? ingoPlaceLabel(ingo.value) : '';
}

/**
 * **Typed address → map coordinates** (route pins, distance): the first of
 * InGo's places for it, or, given a point to measure from (`near`), the
 * nearest of them, as the suggestions are ordered (5 Oct 2026: "seeta" typed
 * and never picked placed Entebbe's Seeta, the search's first, 70 km from a
 * client with three nearer). Caller: **`RequestDeliveryPage`**.
 *
 * @param {string} query
 * @param {{ near?: { lat: number, lng: number } | null }} [options]
 * @returns {Promise<{ lat: number, lng: number } | null>}
 */
export async function forwardGeocodeAddress(query, options = {}) {
  const q = String(query || '').trim();
  if (!q) return null;
  const words = await loadCountryWords();
  // Country-only queries resolve to a national centroid — useless for routes.
  if (isCoarsePlaceLabel(q, words)) return null;
  // "Unnamed Road" forward-geocodes unpredictably and caused ~300km fake distances.
  if (isUnrecognizedPlaceLabel(q, words)) return null;

  // "lat, lng" from GPS fallback — use exact coordinates (no geocode round-trip).
  const coordMatch = q.match(/^(-?\d+(?:\.\d+)?)\s*,\s*(-?\d+(?:\.\d+)?)$/);
  if (coordMatch) {
    const lat = Number(coordMatch[1]);
    const lng = Number(coordMatch[2]);
    if (Number.isFinite(lat) && Number.isFinite(lng) && Math.abs(lat) <= 90 && Math.abs(lng) <= 180) {
      return { lat, lng };
    }
  }

  const ingo = await ingoPlaces(() => booking.searchPlaces(q));
  const places = ingo?.value ?? [];
  if (places.length === 0) return null;

  const near = options.near;
  const away = (p) => (near ? (haversineKm(near.lat, near.lng, p.latitude, p.longitude) ?? Infinity) : 0);
  const chosen = near && Number.isFinite(near.lat) && Number.isFinite(near.lng)
    ? places.reduce((best, p) => (away(p) < away(best) ? p : best))
    : places[0];

  return { lat: chosen.latitude, lng: chosen.longitude };
}
